import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, Hotel as HotelIcon, Palmtree, Car, MapPin, Search, Users, CalendarDays, Landmark, Armchair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AirportAutocomplete } from "@/components/AirportAutocomplete";

const tabs = [
  { key: "flights", label: "Flights", icon: Plane },
  { key: "hotels", label: "Hotels", icon: HotelIcon },
  { key: "holidays", label: "Holidays", icon: Palmtree },
  { key: "car-hire", label: "Car Hire", icon: Car },
  { key: "umrah", label: "Umrah & Hajj", icon: Landmark },
];

const Field = ({ icon: Icon, label, children }) => (
  <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1]">
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
    {children}
  </div>
);

const inputCls = "w-full outline-none text-sm font-medium text-slate-900 placeholder:text-slate-400 bg-transparent";

export const SearchWidget = () => {
  const [tab, setTab] = useState("flights");
  const [origin, setOrigin] = useState(null);
  const [dest, setDest] = useState(null);
  const [cityText, setCityText] = useState("");
  const [date, setDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [cabin, setCabin] = useState("Economy");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [paxOpen, setPaxOpen] = useState(false);
  const nav = useNavigate();

  const isAir = tab === "flights" || tab === "car-hire";
  const totalPax = adults + children + infants;

  const search = () => {
    const p = new URLSearchParams();
    if (tab === "flights") {
      if (origin?.code) p.set("origin", origin.code);
      if (dest?.code) p.set("destination", dest.code);
      p.set("cabin", cabin);
      if (date) p.set("depart_date", date);
      if (returnDate) p.set("return_date", returnDate);
      p.set("adults", adults); p.set("children", children); p.set("infants", infants);
      nav(`/flights?${p.toString()}`);
      return;
    } else if (tab === "hotels") {
      if (cityText) p.set("city", cityText);
    } else if (tab === "holidays") {
      if (cityText) p.set("destination", cityText);
    } else if (tab === "car-hire") {
      if (origin?.code) p.set("origin", origin.code);
    }
    nav(`/${tab}?${p.toString()}`);
  };

  const Stepper = ({ label, hint, value, set, min }) => (
    <div className="flex items-center justify-between py-2">
      <div><p className="text-sm font-medium text-slate-800">{label}</p><p className="text-[11px] text-slate-400">{hint}</p></div>
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`pax-${label.toLowerCase()}-minus`} onClick={() => set(Math.max(min, value - 1))} className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold leading-none hover:border-[#FF6B35] disabled:opacity-40" disabled={value <= min}>−</button>
        <span data-testid={`pax-${label.toLowerCase()}-value`} className="w-5 text-center font-semibold text-slate-900">{value}</span>
        <button type="button" data-testid={`pax-${label.toLowerCase()}-plus`} onClick={() => set(value + 1)} className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold leading-none hover:border-[#FF6B35]">+</button>
      </div>
    </div>
  );

  return (
    <div data-testid="search-widget" className="bg-white rounded-3xl shadow-[0_20px_60px_rgb(11,60,93,0.15)] p-4 sm:p-6 border border-slate-100">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-3 mb-4 border-b border-slate-100">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#0B3C5D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-3 items-stretch">
        {isAir && (
          <Field icon={MapPin} label={tab === "flights" ? "From" : "Pick-up airport"}>
            <AirportAutocomplete testid="search-origin" placeholder="City or airport" value={origin ? `${origin.city || origin.name} (${origin.code})` : ""} onSelect={setOrigin} />
          </Field>
        )}
        {tab === "flights" && (
          <Field icon={MapPin} label="To">
            <AirportAutocomplete testid="search-destination" placeholder="City or airport" value={dest ? `${dest.city || dest.name} (${dest.code})` : ""} onSelect={setDest} />
          </Field>
        )}
        {(tab === "hotels" || tab === "holidays") && (
          <Field icon={MapPin} label="Destination">
            <input data-testid="search-destination-text" className={inputCls} placeholder="e.g. Dubai" value={cityText} onChange={(e) => setCityText(e.target.value)} />
          </Field>
        )}
        {tab === "flights" && (
          <Field icon={Armchair} label="Cabin">
            <select data-testid="search-cabin" className={inputCls} value={cabin} onChange={(e) => setCabin(e.target.value)}>
              <option>Economy</option><option>Premium Economy</option><option>Business</option><option>First</option>
            </select>
          </Field>
        )}
        <Field icon={CalendarDays} label={tab === "flights" ? "Depart" : "Date"}>
          <input data-testid="search-date" type="date" min={new Date().toISOString().slice(0, 10)} className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {tab === "flights" && (
          <Field icon={CalendarDays} label="Return">
            <input data-testid="search-return" type="date" min={date || new Date().toISOString().slice(0, 10)} className={inputCls} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
          </Field>
        )}
        {tab !== "umrah" && (
          <div className="flex-1 min-w-[150px] relative">
            <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1] cursor-pointer" onClick={() => setPaxOpen((o) => !o)} data-testid="search-pax-toggle">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Travellers</div>
              <p className="text-sm font-medium text-slate-900">{totalPax} traveller{totalPax !== 1 ? "s" : ""}</p>
            </div>
            {paxOpen && (
              <div className="absolute z-50 mt-2 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-2xl p-3" data-testid="search-pax-panel">
                <Stepper label="Adults" hint="12+ years" value={adults} set={setAdults} min={1} />
                <Stepper label="Children" hint="2–11 years" value={children} set={setChildren} min={0} />
                <Stepper label="Infants" hint="Under 2" value={infants} set={setInfants} min={0} />
                <button type="button" onClick={() => setPaxOpen(false)} className="w-full mt-2 rounded-full bg-[#0B3C5D] text-white text-sm font-semibold py-2" data-testid="search-pax-done">Done</button>
              </div>
            )}
          </div>
        )}
        <Button data-testid="search-submit" onClick={search}
          className="rounded-xl bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-8 h-auto py-4 md:self-stretch">
          <Search className="w-5 h-5 mr-2" /> Search
        </Button>
      </div>
    </div>
  );
};
